import React from 'react'
import { Select, Typography } from 'antd'
import { Bar } from 'react-chartjs-2'
import Table from '../../../components/lists/Table/Table'
import ExcelExporter from '../../../components/ExcelExporter'
import ColumnSelector from './ColumnSelector'
import {
  createWebsiteColumns,
  getWebsiteColumnOptions
} from './columnConfigs'
import {
  calculateTeaserTotals,
  createTeaserSummaryExportRow
} from './utils'
import { roundTo } from './calculations'
import { CompanyType } from '../../../__generated__/graphql'
import { fetchGenericDateBreakdown, WEBSITE_COLUMN_CONFIG } from './dateBreakdownExportGeneric'
import { HIDDEN_CONVERSION_COLUMN_VALUES } from './conversionReportingGate'

const { Title, Text } = Typography

// Generate website comparison chart data
const generateWebsiteChartData = (websiteData: any[], selectedMetric: string) => {
  // Get top 5 websites by the selected metric
  const topWebsites = websiteData
    .filter(website => (website[selectedMetric] || 0) > 0)
    .sort((a, b) => (b[selectedMetric] || 0) - (a[selectedMetric] || 0))
    .slice(0, 5)

  if (topWebsites.length === 0) {
    return null
  }

  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6']
  
  const chartData = {
    labels: topWebsites.map(website => 
      website.name.length > 15 ? `${website.name.substring(0, 15)}...` : website.name
    ),
    datasets: [{
      label: `${selectedMetric} by Website`,
      data: topWebsites.map(website => website[selectedMetric] || 0),
      backgroundColor: colors.slice(0, topWebsites.length),
      borderColor: colors.slice(0, topWebsites.length),
      borderWidth: 2,
      fill: false
    }]
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: true,
        text: `Top Websites by ${selectedMetric}`
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: selectedMetric
        }
      },
      x: {
        title: {
          display: true,
          text: 'Websites'
        }
      }
    }
  }

  return { chartData, options }
}

// Get available website metrics that have actual non-zero values
const getAvailableWebsiteMetrics = (websiteData: any[], companyType: string) => {
  const baseMetrics = [
    { label: 'Reads', value: 'reads' },
    { label: 'Clicks', value: 'tdClicks' },
    { label: 'Impressions', value: 'tdImpressions' }
  ]

  // Check totals for each metric to see if they have meaningful data
  const availableMetrics = baseMetrics.filter(metric => {
    const total = websiteData.reduce((sum, website) => {
      const value = website[metric.value] || 0
      return sum + (typeof value === 'number' ? value : 0)
    }, 0)
    return total > 0
  })

  // Add company-specific metrics only if they have data
  const publisherRevenue = websiteData.reduce((sum, website) => sum + (website.publisherRevenue || 0), 0)
  const totalCost = websiteData.reduce((sum, website) => sum + (website.totalCost || 0), 0)
  const conversions = websiteData.reduce((sum, website) => sum + (website.conversions || 0), 0)

  if (companyType === CompanyType.Publisher && publisherRevenue > 0) {
    availableMetrics.push({ label: 'Revenue', value: 'publisherRevenue' })
  } else if (companyType !== CompanyType.Publisher && totalCost > 0) {
    availableMetrics.push({ label: 'Total Cost', value: 'totalCost' })
  }

  if (conversions > 0) {
    availableMetrics.push({ label: 'Conversions', value: 'conversions' })
  }

  return availableMetrics
}

interface WebsitePerformanceProps {
  websiteVisibleColumns: string[]
  onWebsiteVisibleColumnsChange: (columns: string[]) => void
  selectedWebsiteMetric: string
  onSelectedWebsiteMetricChange: (metric: string) => void
  companyType: string
  websiteData?: any[]
  cubejsApi?: any
  campaignId?: string
  formattedDateRange?: string[]
  aggPublisherFilters?: any[]
  hideConversionColumns?: boolean
}

const WebsitePerformance: React.FC<WebsitePerformanceProps> = ({
  websiteVisibleColumns,
  onWebsiteVisibleColumnsChange,
  selectedWebsiteMetric,
  onSelectedWebsiteMetricChange,
  companyType,
  websiteData: realWebsiteData,
  cubejsApi,
  campaignId,
  formattedDateRange,
  aggPublisherFilters = [],
  hideConversionColumns = false
}) => {
  const effectiveWebsiteVisibleColumns = hideConversionColumns
    ? websiteVisibleColumns.filter(column => !HIDDEN_CONVERSION_COLUMN_VALUES.includes(column))
    : websiteVisibleColumns

  // Use real data only - no fallback to mock data
  const finalWebsiteData = realWebsiteData || []

  const websiteTotals = React.useMemo(
    () => calculateTeaserTotals(finalWebsiteData),
    [finalWebsiteData]
  )

  // Debug logging for website data
  console.log('Debug: Website data structure:', finalWebsiteData)
  if (!finalWebsiteData || !Array.isArray(finalWebsiteData)) {
    console.error('Debug: Website data is null or not an array:', finalWebsiteData)
  } else {
    finalWebsiteData.forEach((website, index) => {
      if (!website.children) {
        console.error(`Debug: Website at index ${index} has null/undefined children:`, website)
      } else if (!Array.isArray(website.children)) {
        console.error(`Debug: Website at index ${index} has non-array children:`, website.children)
      }
    })
  }

  // Only show metrics that have actual data
  const websiteMetricOptions = getAvailableWebsiteMetrics(finalWebsiteData, companyType)
  
  // Ensure selectedWebsiteMetric is valid, if not, use the first available metric
  const validSelectedWebsiteMetric = websiteMetricOptions.find(m => m.value === selectedWebsiteMetric)?.value || websiteMetricOptions[0]?.value || 'reads'
  
  // Auto-correct the selected metric if it's invalid
  React.useEffect(() => {
    if (websiteMetricOptions.length > 0 && !websiteMetricOptions.find(m => m.value === selectedWebsiteMetric)) {
      onSelectedWebsiteMetricChange(websiteMetricOptions[0].value)
    }
  }, [websiteMetricOptions, selectedWebsiteMetric, onSelectedWebsiteMetricChange])

  // Generate chart data
  const websiteChart = generateWebsiteChartData(finalWebsiteData, validSelectedWebsiteMetric)

  // Create and filter website columns
  const allWebsiteColumns = createWebsiteColumns()
  const websiteColumns = allWebsiteColumns.filter(({ key, dataIndex }) =>
    effectiveWebsiteVisibleColumns.includes(dataIndex || '') || key === 'actions'
  )

  const websiteColumnMapping = React.useMemo(() => (
    allWebsiteColumns
      .filter(col => col.dataIndex && effectiveWebsiteVisibleColumns.includes(col.dataIndex))
      .filter(col => typeof col.title === 'string')
      .map(col => ({
        title: col.title as string,
        dataIndex: col.dataIndex!
      }))
  ), [allWebsiteColumns, effectiveWebsiteVisibleColumns])

  const websiteSummaryRowForExport = React.useMemo(() => {
    if (!finalWebsiteData.length) {
      return undefined
    }

    return createTeaserSummaryExportRow(websiteTotals, companyType)
  }, [companyType, finalWebsiteData.length, websiteTotals])

  // Handler to fetch website date breakdown data before export
  const handleWebsiteBeforeExport = async () => {
    if (!cubejsApi || !campaignId || !formattedDateRange) {
      console.warn('Missing required props for website date breakdown export')
      return []
    }

    try {
      const dateBreakdownData = await fetchGenericDateBreakdown(
        cubejsApi,
        campaignId,
        formattedDateRange,
        aggPublisherFilters,
        effectiveWebsiteVisibleColumns,
        companyType,
        WEBSITE_COLUMN_CONFIG,
        'website',
        (utmSource) => utmSource === '' || utmSource === 'ADSERVER' // Only teasers
      )

      return [
        {
          name: 'Date Breakdown',
          data: dateBreakdownData,
        }
      ]
    } catch (error) {
      console.error('Error fetching website date breakdown data:', error)
      return []
    }
  }

  const getWebsiteSummaryData = () => {
    const totals = websiteTotals
    const eCpmValue = totals.tdImpressions > 0
      ? (companyType === CompanyType.Publisher
        ? roundTo(totals.publisherRevenue / (totals.tdImpressions / 1000), 2)
        : roundTo(totals.totalCost / (totals.tdImpressions / 1000), 2))
      : 0
    const cpaValue = totals.conversions > 0 ? roundTo(totals.totalCost / totals.conversions, 2) : 0
    const impsReadsValue = totals.tdImpressions > 0 ? roundTo((totals.reads / totals.tdImpressions) * 1000, 2) : 0
    const cotrValue = totals.reads > 0 ? roundTo((totals.clickOuts / totals.reads) * 100, 2) : 0
    const ctrValue = totals.tdImpressions > 0 ? roundTo((totals.tdClicks / totals.tdImpressions) * 100, 2) : 0

    return {
      name: 'Total',
      tdImpressions: totals.tdImpressions.toLocaleString(),
      tdClicks: totals.tdClicks.toLocaleString(),
      reads: totals.reads.toLocaleString(),
      publisherRevenue: (
        <span>
          <span className="text-xs text-gray-500">DKK </span>
          {totals.publisherRevenue.toFixed(2)}
        </span>
      ),
      totalCost: (
        <span>
          <span className="text-xs text-gray-500">DKK </span>
          {totals.totalCost.toFixed(2)}
        </span>
      ),
      clickOuts: totals.clickOuts.toLocaleString(),
      conversions: totals.conversions,
      ctr: `${ctrValue}%`,
      eCpm: totals.tdImpressions > 0 ? (
        <span>
          <span className="text-xs text-gray-500">DKK </span>
          {eCpmValue.toFixed(2)}
        </span>
      ) : '-',
      cpa: totals.conversions > 0 ? (
        <span>
          <span className="text-xs text-gray-500">DKK </span>
          {cpaValue.toFixed(2)}
        </span>
      ) : '-',
      imps_reads: `${impsReadsValue}‰`,
      cotr: `${cotrValue}%`
    }
  }

  return (
    <div className="space-y-6">
      {/* Website Performance Table */}
      <div>
        <div className="flex justify-between items-center mb-3">
          <div>
            <Title level={4} className="!mb-1">Website Performance</Title>
            <Text type="secondary">Performance breakdown by website with nested teasers</Text>
          </div>
          <ColumnSelector
            options={getWebsiteColumnOptions(companyType, hideConversionColumns)}
            value={effectiveWebsiteVisibleColumns}
            onChange={onWebsiteVisibleColumnsChange}
          />
        </div>
        <Table
          columns={websiteColumns}
          data={finalWebsiteData}
          searchable={false}
          showSummary
          summaryData={getWebsiteSummaryData()}
          footer={() => (
            <ExcelExporter
              data={finalWebsiteData}
              visibleColumns={effectiveWebsiteVisibleColumns}
              columnMapping={websiteColumnMapping}
              summaryRow={websiteSummaryRowForExport}
              filename="WebsitesData"
              buttonText="Export to Excel"
              onBeforeExport={handleWebsiteBeforeExport}
              exportingText="Preparing export with date breakdown..."
            />
          )}
        />
      </div>

      {/* Website Performance Chart */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <Title level={4} className="!mb-1">Website Performance Chart</Title>
            <Text type="secondary" className="text-sm">
              Top performing websites by {websiteMetricOptions.find(m => m.value === validSelectedWebsiteMetric)?.label?.toLowerCase() || 'metric'}
            </Text>
          </div>
          {websiteMetricOptions.length > 0 && (
            <div className="flex items-center gap-3">
              <Text strong className="text-sm font-medium text-gray-700">Metric:</Text>
              <Select
                value={validSelectedWebsiteMetric}
                onChange={onSelectedWebsiteMetricChange}
                className="w-40 [&_.ant-select-selector]:!border-gray-300 [&_.ant-select-selector]:!rounded-lg [&_.ant-select-selector]:!shadow-sm hover:[&_.ant-select-selector]:!border-blue-400 focus:[&_.ant-select-selector]:!border-blue-500 focus:[&_.ant-select-selector]:!ring-2 focus:[&_.ant-select-selector]:!ring-blue-100"
                size="middle"
                options={websiteMetricOptions}
              />
            </div>
          )}
        </div>
        <div className="h-96">
          {(() => {
            if (websiteMetricOptions.length === 0) {
              return (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center text-gray-500">
                    <div className="text-xl mb-2">No Website Performance Data Available</div>
                    <div className="text-sm">No websites have measurable performance metrics for this campaign.</div>
                  </div>
                </div>
              )
            }

            if (!websiteChart) {
              return (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center text-gray-500">
                    <div className="text-lg mb-2">No data available</div>
                    <div className="text-sm">No websites have data for {websiteMetricOptions.find(m => m.value === validSelectedWebsiteMetric)?.label}</div>
                  </div>
                </div>
              )
            }

            return <Bar data={websiteChart.chartData} options={websiteChart.options} />
          })()}
        </div>
      </div>
    </div>
  )
}

export default WebsitePerformance